// File: src/utils/championships.js
// How far an event reaches.
//
// Most races belong to one country's calendar. A European or World championship
// belongs on every one of them, which is the only reason this field exists - so
// a Finnish visitor sees the Worlds being held in Sweden.
//
// Must match the events.championship ENUM and CHAMPIONSHIPS in backend/events.js.

import { useCallback } from 'react';
import { useLanguage } from '../contexts/LanguageContext';

export const CHAMPIONSHIPS = ['none', 'european', 'world'];

// 'none' is an ordinary race. Everything reads better as a question about the
// event than as a comparison against a magic string.
export const isChampionship = (value) => Boolean(value) && value !== 'none';

const LABEL_KEYS = {
  none: 'championshipNone',
  european: 'championshipEuropean',
  world: 'championshipWorld'
};

// Tailwind cannot see class names built at runtime, so each is written in full.
// Gold for the Worlds, blue for the Europeans - and both loud enough to explain
// why a race in another country is on this calendar.
export const CHAMPIONSHIP_BADGE_CLASSES = {
  european: 'bg-blue-100 dark:bg-blue-500/20 text-blue-700 dark:text-blue-400',
  world: 'bg-yellow-100 dark:bg-yellow-500/20 text-yellow-700 dark:text-yellow-400'
};

export const championshipBadgeClass = (value) =>
  CHAMPIONSHIP_BADGE_CLASSES[value] || CHAMPIONSHIP_BADGE_CLASSES.european;

/** The name of a championship level in the language on screen. */
export function useChampionshipLabel() {
  const { t } = useLanguage();
  return useCallback(
    (value) => (LABEL_KEYS[value] ? t(LABEL_KEYS[value]) : value || ''),
    [t]
  );
}
